import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { Exposure, Peril, PerilCreateRequest } from '../../../types/policy';
import { RenewalExposureUpdateRequest } from '../../../types/renewals';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import { getUpdatedExposureObj } from '../util_sharedEndorsementAndRenewal';
import AddPerilModal from './AddRenewalPerilModal';
import PerilsList from './PerilsList';

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
}

/**
 * Peril list for an existing exposure on an renewal
 */
const ExistingExposurePerils = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [removedPeril, setRemovePeril] = React.useState<
		Peril | PerilCreateRequest
	>(null);
	const { renewalData, authData } = props;

	const { renewalUpdateObj, productConfig, setLoading, saveFastForm } =
		renewalData;

	const selectedExposure = renewalData.selectedExposure as Exposure;

	const exposureRequest = getUpdatedExposureObj(
		renewalUpdateObj,
		selectedExposure.locator,
	) as RenewalExposureUpdateRequest;

	const isInEditMode =
		authData.hasPermission('renewal.update') &&
		renewalUpdateObj.state === 'application';
	const endPerils = exposureRequest.endPerils
		? [...exposureRequest.endPerils]
		: [];
	const addPerils = exposureRequest.addPerils
		? [...exposureRequest.addPerils]
		: [];

	const allRenewalPerils = [...selectedExposure.perils, ...addPerils].filter(
		(peril) => {
			return !endPerils.find(
				(endPerilLocator) => endPerilLocator === peril.locator,
			);
		},
	);

	const removePeril = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			const perilLocator = removedPeril.locator;
			const exposureLocator = selectedExposure.locator;
			const perilIsAdded = addPerils.some((peril: PerilCreateRequest) => {
				return peril.locator === perilLocator;
			});
			// peril is either existing or added; send options object accordingly
			const removeOptions = perilIsAdded
				? {
						removeAddedPeril: {
							perilLocator,
							exposureLocator,
							exposureIsAdded: false,
						},
				  }
				: {
						removePeril: { perilLocator, exposureLocator },
				  };
			await saveFastForm(getValues(), dirtyFields, reset, removeOptions);
			successToast('Successfully removed peril');
		} catch (error) {
			errorToast('Error removing peril');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(e) => e.name === selectedExposure.name,
	);
	const perilConfigs = exposureConfig.perils;

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.locator}
			/>
		);
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={removedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const openConfirmationModal = (peril: Peril | PerilCreateRequest) => {
		setIsConfirmModalOpen(true);
		setRemovePeril(peril);
	};

	return (
		<>
			{isConfirmModalOpen && renderRemovePerilModal()}
			<StyledTitleContainer>
				<StyledTitle>{t('peril.plural.upper')}</StyledTitle>
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<PerilsList
					perils={allRenewalPerils}
					openConfirmationModal={openConfirmationModal}
				/>
			</StyledSectionContainer>
			{isInEditMode && renderActions()}
		</>
	);
};

export default withRenewalContext<Props>(ExistingExposurePerils);
